import React, { useEffect, useMemo, useState } from 'react';
import { Button } from './ui/button';
import { Camera, Calendar } from 'lucide-react';

const HERO_POSTER_KEY = 'hero_last_loaded_image';

const isRenderableImageUrl = (value) =>
  typeof value === 'string' &&
  value.length > 0 &&
  (value.startsWith('http://') ||
    value.startsWith('https://') ||
    value.startsWith('data:') ||
    value.startsWith('blob:'));

const uniqueUrls = (items) => [...new Set((items || []).filter(isRenderableImageUrl))];

const Hero = ({ onBookNow, sliderImages }) => {
  const candidateImages = useMemo(() => uniqueUrls(sliderImages || []), [sliderImages]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [readyImages, setReadyImages] = useState([]);
  const [isCurrentImageLoaded, setIsCurrentImageLoaded] = useState(false);
  const [loadedImage, setLoadedImage] = useState(() => {
    try {
      const cached = localStorage.getItem(HERO_POSTER_KEY);
      return isRenderableImageUrl(cached) ? cached : '';
    } catch (error) {
      return '';
    }
  });

  useEffect(() => {
    let cancelled = false;

    if (!candidateImages.length) {
      setReadyImages([]);
      return undefined;
    }

    const nextReady = [];

    candidateImages.slice(0, 4).forEach((imageUrl) => {
      const preloadImage = new Image();
      preloadImage.fetchPriority = 'high';
      preloadImage.decoding = 'async';
      preloadImage.onload = () => {
        if (cancelled) return;
        nextReady.push(imageUrl);
        setReadyImages((prev) => {
          const merged = [...prev, imageUrl];
          return uniqueUrls(merged);
        });
      };
      preloadImage.onerror = () => {};
      preloadImage.src = imageUrl;
    });

    return () => {
      cancelled = true;
    };
  }, [candidateImages]);

  const activeImages = readyImages.length ? readyImages : candidateImages;
  const currentImage = activeImages[currentImageIndex] || activeImages[0];
  const posterImage = loadedImage || currentImage || '';

  useEffect(() => {
    setIsCurrentImageLoaded(false);
  }, [currentImage]);

  useEffect(() => {
    if (!candidateImages.length) return undefined;

    const preloadLinks = candidateImages.slice(0, 2).map((imageUrl) => {
      const existing = document.head.querySelector(`link[rel="preload"][href="${imageUrl}"]`);
      if (existing) return null;

      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = imageUrl;
      document.head.appendChild(link);
      return link;
    });

    return () => {
      preloadLinks.forEach((link) => {
        if (link?.parentNode) {
          link.parentNode.removeChild(link);
        }
      });
    };
  }, [candidateImages]);

  useEffect(() => {
    if (!activeImages.length) return undefined;

    const timer = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % activeImages.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [activeImages.length]);

  useEffect(() => {
    if (currentImageIndex >= activeImages.length) {
      setCurrentImageIndex(0);
    }
  }, [activeImages.length, currentImageIndex]);

  useEffect(() => {
    if (!activeImages.length) return;

    const nextIndex = (currentImageIndex + 1) % activeImages.length;
    const preloadImage = new Image();
    preloadImage.fetchPriority = 'high';
    preloadImage.decoding = 'async';
    preloadImage.src = activeImages[nextIndex];
  }, [activeImages, currentImageIndex]);

  useEffect(() => {
    if (!currentImage) return;
    if (!loadedImage && currentImage) {
      setLoadedImage(currentImage);
    }
  }, [currentImage, loadedImage]);

  if (!currentImage) {
    return (
      <div className="relative h-screen w-full overflow-hidden bg-gray-900">
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/75" />
        <div className="relative z-10 flex h-full flex-col items-center justify-center px-4 text-center">
          <div className="mx-auto max-w-4xl space-y-6 animate-fade-in">
            <div className="mb-4 flex items-center justify-center gap-2 text-white/90">
              <Camera className="h-6 w-6" />
              <span className="text-sm font-light uppercase tracking-widest">Professional Wedding Photography</span>
            </div>

            <h1 className="text-5xl font-bold leading-tight text-white md:text-7xl lg:text-8xl">
              Capturing Your
              <br />
              <span className="bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent">
                Perfect Moments
              </span>
            </h1>

            <p className="mx-auto max-w-2xl text-xl font-light text-white/90 md:text-2xl">
              Timeless memories beautifully preserved through the art of photography
            </p>

            <div className="flex flex-col items-center justify-center gap-4 pt-8 sm:flex-row">
              <Button
                size="lg"
                onClick={onBookNow}
                className="bg-white px-8 py-6 text-lg font-medium text-black shadow-xl transition-colors duration-200 hover:bg-white/90"
              >
                <Calendar className="mr-2 h-5 w-5" />
                Book Your Event
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => document.getElementById('albums')?.scrollIntoView({ behavior: 'smooth' })}
                className="border-2 border-white bg-transparent px-8 py-6 text-lg font-medium text-white transition-colors duration-200 hover:bg-white hover:text-black"
              >
                View Portfolio
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-screen w-full overflow-hidden">
      <div
        className="absolute inset-0 bg-gray-900"
        style={
          posterImage
            ? {
                backgroundImage: `url(${posterImage})`,
                backgroundPosition: 'center',
                backgroundSize: 'cover'
              }
            : undefined
        }
      >
        <img
          src={currentImage}
          alt={`Wedding Photography ${currentImageIndex + 1}`}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${
            isCurrentImageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          loading="eager"
          decoding="async"
          fetchPriority="high"
          onLoad={(event) => {
            const nextImage = event.currentTarget.currentSrc || currentImage;
            setIsCurrentImageLoaded(true);
            setLoadedImage(nextImage);
            try {
              localStorage.setItem(HERO_POSTER_KEY, nextImage);
            } catch (error) {
              // Ignore storage issues and keep rendering.
            }
          }}
          onError={() => {
            setIsCurrentImageLoaded(false);
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70" />
      </div>

      <div className="relative z-10 flex h-full flex-col items-center justify-center px-4 text-center">
        <div className="mx-auto max-w-4xl space-y-6 animate-fade-in">
          <div className="mb-4 flex items-center justify-center gap-2 text-white/90">
            <Camera className="h-6 w-6" />
            <span className="text-sm font-light uppercase tracking-widest">Professional Wedding Photography</span>
          </div>

          <h1 className="text-5xl font-bold leading-tight text-white md:text-7xl lg:text-8xl">
            Capturing Your
            <br />
            <span className="bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent">
              Perfect Moments
            </span>
          </h1>

          <p className="mx-auto max-w-2xl text-xl font-light text-white/90 md:text-2xl">
            Timeless memories beautifully preserved through the art of photography
          </p>

          <div className="flex flex-col items-center justify-center gap-4 pt-8 sm:flex-row">
            <Button
              size="lg"
              onClick={onBookNow}
              className="bg-white px-8 py-6 text-lg font-medium text-black shadow-xl transition-colors duration-200 hover:bg-white/90"
            >
              <Calendar className="mr-2 h-5 w-5" />
              Book Your Event
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => document.getElementById('albums')?.scrollIntoView({ behavior: 'smooth' })}
              className="border-2 border-white bg-transparent px-8 py-6 text-lg font-medium text-white transition-colors duration-200 hover:bg-white hover:text-black"
            >
              View Portfolio
            </Button>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
        <div className="flex h-10 w-6 items-start justify-center rounded-full border-2 border-white/50 p-2">
          <div className="h-3 w-1 rounded-full bg-white/50" />
        </div>
      </div>
    </div>
  );
};

export default Hero;
